// src/tabs/beds24/OrphanRow.jsx — Phase 6.A PR4 commit 8 (NIT-1 extraction)
//
// Composant row du tableau "Logements sans mapping Beds24" (zone orphelins)
// dans Paramètres > Beds24. Extrait du parent Beds24SyncPanel.jsx pour
// passer sous 600 lignes (NIT-1 pessimist QA). Aucun changement
// fonctionnel : tests E2E PR4 existants doivent passer SANS MODIFICATION.
//
// DA (CHANTIER Paramètres) : habillage SEUL — pastilles statut sémantiques +
// icônes lucide (emoji→icône §8 ter). Zéro logique/data touchée.
//
// Props :
//   - log                  { id, nom, prop, mapping, config }
//                          (issu de orphanLogs côté parent)
//   - bookingsNoMappingCount  number (pour décider toast vs scroll)
//   - fmtDate              helper formatage date
//   - onEdit({ existing, logement_id_default })
//   - onReactivateMapping(mapping)
//   - onScrollToBookings()       handler scroll vers zone Bookings sans mapping
//   - onFlashToast(kind, msg)    helper toast côté parent (cas "Aucun booking")
//   - styles               { th, td, btnSecondary } (tokens partagés)
//
// Sémantique 3 cas couverts (cf. Beds24SyncPanel BPR4-1 + BPR4-2) :
//   1. Logement V1 + aucun mapping V2          → "Aucun mapping"
//   2. Logement V1 + mapping V2 inactif         → "Mapping désactivé"
//   3. Logement V1 renommé sans MAJ V2          → géré dans tableau
//                                                  Mappings actifs (badge rouge)

import { Pause, AlertTriangle, RotateCcw, Plug, Settings } from "lucide-react";
import { DS } from "../../lib/ui";
import { StatusPill } from "../_paramsUi";

export default function OrphanRow({
  log: l,
  bookingsNoMappingCount,
  fmtDate,
  onEdit,
  onReactivateMapping,
  onScrollToBookings,
  onFlashToast,
  styles,
}) {
  const { td, btnSecondary } = styles;
  const hasInactiveMapping = !!l.mapping && l.mapping.active === false;
  return (
    <tr
      style={{
        borderBottom: `1px solid ${DS.border}`,
        background: DS.warningBg,
      }}
    >
      <td style={td}>
        {l.nom} ({l.prop})
      </td>
      <td style={td}>
        <code
          style={{
            background: "#eef1f4",
            padding: "2px 6px",
            borderRadius: 4,
          }}
        >
          {l.id}
        </code>
      </td>
      <td style={td}>
        <ModeBadge mode={l.config?.pricing_mode} />
      </td>
      <td style={td}>
        {hasInactiveMapping ? (
          <span title={`Mapping désactivé le ${fmtDate(l.mapping.updated_at)}`}>
            <StatusPill tone="warning"><Pause size={11} strokeWidth={2} /> Mapping désactivé</StatusPill>
          </span>
        ) : (
          <StatusPill tone="warning"><AlertTriangle size={11} strokeWidth={2} /> Aucun mapping</StatusPill>
        )}
      </td>
      <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
        {hasInactiveMapping ? (
          <button
            onClick={() => onReactivateMapping(l.mapping)}
            title="Réactiver le mapping Beds24 désactivé"
            style={{
              ...btnSecondary,
              color: DS.marine,
              borderColor: DS.borderHover,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            <RotateCcw size={13} strokeWidth={1.9} /> Réactiver
          </button>
        ) : (
          <button
            onClick={() => {
              if (bookingsNoMappingCount === 0) {
                onFlashToast(
                  "info",
                  `Aucun booking Beds24 reçu pour ${l.id} — vérifie l'iCal/Beds24 côté plateforme avant.`,
                );
              } else {
                onScrollToBookings();
              }
            }}
            title="Aller à la zone Bookings sans mapping"
            style={{ ...btnSecondary, display: "inline-flex", alignItems: "center", gap: 5 }}
          >
            <Plug size={13} strokeWidth={1.9} /> Configurer Beds24
          </button>
        )}
        <button
          onClick={() =>
            onEdit({
              existing: l.config,
              logement_id_default: l.id,
            })
          }
          title={
            l.config ? "Éditer le mode pricing" : "Créer la config pricing"
          }
          style={{ ...btnSecondary, marginLeft: 6, display: "inline-flex", alignItems: "center", gap: 5 }}
        >
          <Settings size={13} strokeWidth={1.9} /> {l.config ? "Éditer pricing" : "Configurer pricing"}
        </button>
      </td>
    </tr>
  );
}

// PR4 §3.4 : 3 badges Mode pricing (DS tokens) — dupliqué depuis MappingRow.jsx
// pour découplage (composants indépendants, pas d'import croisé).
function ModeBadge({ mode }) {
  if (!mode)
    return <StatusPill tone="warning"><AlertTriangle size={11} strokeWidth={2} /> non configuré</StatusPill>;
  if (mode === "pricelabs")
    return <StatusPill tone="info">PriceLabs</StatusPill>;
  if (mode === "manual")
    return <StatusPill tone="success">Manual</StatusPill>;
  return <span style={{ color: DS.textMuted }}>—</span>;
}
